/*
 * <volume-tiers> — the offer console on the product page.
 *
 * Picking a pack drives the real quantity the form submits: it writes to the
 * theme's quantity input when that block is on the page, and otherwise keeps a
 * hidden quantity input on the add-to-cart form in sync.
 *
 * It also mirrors the selected pack's total into the main price block, using
 * the money strings Liquid already formatted onto each row — so the headline
 * price always matches the pack that is selected.
 *
 * In variant mode each rung stands for a product variant. Selecting one clicks
 * the theme's own (hidden) variant radio rather than reimplementing variant
 * selection, so Dawn's price, media, availability and analytics all keep
 * working; quantity stays at 1 because the pack size is in the variant.
 *
 */
class VolumeTiers extends HTMLElement {
  connectedCallback() {
    this.tiers = Array.from(this.querySelectorAll('[data-tier-qty]'));
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // A variant change re-renders the price block from the server at unit
    // price, and leaves these rows showing the old variant's totals. Pull both
    // back in sync from the freshly fetched markup.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const source = data?.html?.querySelector(`volume-tiers[data-section="${this.dataset.section}"]`);
        if (source) this.refreshFrom(source);
      });
    }

    const selected = this.querySelector('.is-selected');
    if (selected) this.select(selected, true);
  }

  get isVariantMode() {
    return this.hasAttribute('data-variant-mode');
  }

  get selectableTiers() {
    return this.tiers.filter((tier) => !tier.disabled);
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  refreshFrom(source) {
    const list = this.querySelector('.deal__chips');
    const next = source.querySelector('.deal__chips');
    if (!list || !next) return;

    const selectedQty = this.querySelector('.is-selected')?.dataset.tierQty;
    list.innerHTML = next.innerHTML;
    this.tiers = Array.from(this.querySelectorAll('[data-tier-qty]'));

    // In variant mode the server markup already knows which variant is live, so
    // adopt its choice. Re-selecting here would click the variant input that
    // triggered this refresh in the first place.
    const restored = this.isVariantMode
      ? this.querySelector('.is-selected') || this.tiers[0]
      : this.tiers.find((el) => el.dataset.tierQty === selectedQty) || this.tiers[0];

    if (restored) this.select(restored, true);
  }

  get form() {
    return (
      document.querySelector(`#product-form-${this.dataset.section}`) ||
      document.querySelector('form[data-type="add-to-cart-form"]')
    );
  }

  onClick(event) {
    const tier = event.target.closest('[data-tier-qty]');
    if (tier) this.select(tier);
  }

  onKeydown(event) {
    const isVertical = event.key === 'ArrowDown' || event.key === 'ArrowUp';
    const isHorizontal = event.key === 'ArrowRight' || event.key === 'ArrowLeft';
    if (!isVertical && !isHorizontal) return;

    const reachable = this.selectableTiers;
    const current = reachable.indexOf(document.activeElement.closest('[data-tier-qty]'));
    if (current === -1 || reachable.length === 0) return;

    event.preventDefault();
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const next = reachable[(current + step + reachable.length) % reachable.length];
    next.focus();
    this.select(next);
  }

  /* `silent` updates the visuals without re-driving the variant picker. */
  select(tier, silent = false) {
    this.tiers.forEach((el) => {
      const active = el === tier;
      el.classList.toggle('is-selected', active);
      el.setAttribute('aria-checked', active ? 'true' : 'false');
    });

    if (this.isVariantMode) {
      if (!silent) this.applyVariant(tier);
      this.apply(1);
    } else {
      this.apply(parseInt(tier.dataset.tierQty, 10));
    }

    this.applyPrice(tier);
    this.applyReadout(tier);
    this.applyNudge(tier);
  }

  /*
   * Chips carry their own figures now, so the only thing left to drive is how
   * far the meter has run toward the best per-unit price.
   */
  applyReadout(tier) {
    const meter = this.querySelector('[data-deal-meter-out]');
    if (meter) meter.style.setProperty('--deal-meter', `${tier.dataset.dealMeter || 0}%`);
  }

  /*
   * Drive the theme's variant radio for this rung. Clicking it (rather than
   * setting .checked) is what makes <variant-selects> hear a change event and
   * run its normal update, so nothing here has to know about variant plumbing.
   */
  applyVariant(tier) {
    const id = tier.dataset.optionValueId;
    if (!id) return;

    const input = document.querySelector(
      `#variant-selects-${this.dataset.section} input[data-option-value-id="${id}"]`
    );
    if (input && !input.checked) input.click();
  }

  /*
   * The momentum line, e.g. "Pack of 3 brings it down to Rs. 425 each".
   * Liquid writes it per row, so no pricing logic lives here.
   */
  applyNudge(tier) {
    const nudge = this.querySelector('[data-packs-nudge]');
    if (!nudge) return;

    const text = tier.dataset.tierNudge || '';
    nudge.textContent = text;
    nudge.classList.toggle('is-empty', text === '');
  }

  /*
   * Rewrite the headline price as the pack total. Both figures come from
   * Liquid, so currency format and the shop's money settings are respected.
   */
  applyPrice(tier) {
    const total = tier.dataset.tierTotal;
    if (!total) return;

    const compare = tier.dataset.tierCompare;
    const block = document.getElementById(`price-${this.dataset.section}`);
    if (!block) return;

    block.querySelectorAll('.price-item--sale, .price-item--regular').forEach((item) => {
      // <s> holds the compare-at price, everything else the price being charged.
      if (item.tagName !== 'S') {
        item.textContent = total;
      } else if (compare) {
        item.textContent = compare;
      }
    });
  }

  apply(quantity) {
    if (!quantity || quantity < 1) return;

    // Preferred path: the theme's own quantity input, so its rules and the
    // "in cart" messaging stay correct.
    const input = document.querySelector(`#Quantity-${this.dataset.section}`);
    if (input) {
      input.value = quantity;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const form = this.form;
    if (!form) return;

    let hidden = form.querySelector('input[name="quantity"]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'quantity';
      form.appendChild(hidden);
    }
    hidden.value = quantity;
  }
}

if (!customElements.get('volume-tiers')) {
  customElements.define('volume-tiers', VolumeTiers);
}
